import Controller from 'controllers/controller';

// Utility controllers
import HistoryController from 'controllers/history';
import BreakpointController from 'controllers/breakpoint';
import ObjectFitController from 'controllers/object-fit';
import LazyLoadController from 'controllers/lazy-load';
import LazySvgsController from 'controllers/lazy-svgs';
import NavController from 'controllers/nav';
import MobileNavController from 'controllers/mobile-nav';
import CookieConsentController from 'controllers/cookie-consent';
import BookingController from 'controllers/booking';
import BlogController from 'controllers/blog';

// Higher level component controllers.
// These instantiate controllers for each individual instance of a component.
import ModalsController from 'controllers/modals';
import CarouselsController from 'controllers/carousels';
import FadingGalleriesController from 'controllers/fading-galleries';
import TabsComponentsController from 'controllers/tabs-components';

// Template specific controllers
import HomeTemplateController from 'controllers/templates/home';
import ContactTemplateController from 'controllers/templates/contact';
import SuitesTemplateController from 'controllers/templates/suites';
import RoomsTemplateController from 'controllers/templates/rooms';
import InspiredEventsTemplateController from 'controllers/templates/inspired-events';

import Helpers from 'tools/helpers';
import Bus from 'tools/bus';

class AppController extends Controller {
    ready() {
        // Set the app of this controller to this
        this.app = this;
        
        this.pageTemplate = document.body.getAttribute('data-template');
        
        this.IEVersion = Helpers.detectIE();

        this.isSafari = !! navigator.userAgent.match(/Version\/[\d\.]+.*Safari/);
        this.iOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && ! window.MSStream;
        
        // To reliably determine if the current device is a touch device, we set an event listener for the touchstart
        // event, and as soon as it fires we add the 'touch-enabled' class to the body.
        this.isTouchDevice = false;
        
        this.helpers = Helpers;
        this.bus = new Bus(this);
        
        this.attachEvents();
        
        this.addIEClasses();
        this.initControllers();
        
        // Load other pages once instantiation is complete
        // this.loaderController.loadPages();
    }

    initControllers() {
        // Utility controllers
        this.historyController = new HistoryController(this);
        this.breakpointController = new BreakpointController(this);
        this.lazyLoadController = new LazyLoadController(this);
        this.lazySvgsController = new LazySvgsController(this);
        this.objectFitController = new ObjectFitController(this);
        this.navController = new NavController(this);
        this.mobileNavController = new MobileNavController(this);
        this.cookieConsentController = new CookieConsentController(this);

        // Component controllers
        this.modalsController = new ModalsController(this);
        this.carouselsController = new CarouselsController(this);
        this.fadingGalleriesController = new FadingGalleriesController(this);
        this.tabsComponentsController = new TabsComponentsController(this);

        // Lone Wolves
        this.bookingController = new BookingController(this);
        this.blogController = new BlogController(this);

        // Template-specific controller
        this.homeTemplateController = new HomeTemplateController(this);
        this.contactTemplateController = new ContactTemplateController(this);
        this.suitesTemplateController = new SuitesTemplateController(this);
        this.roomsTemplateController = new RoomsTemplateController(this);
        this.inspiredEventsTemplateController = new InspiredEventsTemplateController(this);

        this.bus.emit('controllers-initialised');
    }

    attachEvents() {
        window.addEventListener('touchstart', this.onWindowTouchStart);

        this.bus.on('controllers-initialised', this.onControllersInitialised);
    }

    async onTypekitActive() {
        console.log('on Typekit active');

        this.emitDomChange();

        await Helpers.sleep(500);

        this.emitDomChange();
    }

    async onControllersInitialised() {
        this.emitDomChange();

        await Helpers.sleep(500);

        this.emitDomChange();
    }

    /**
     * When called, all controllers that hook into this event will re-initialise in a coordinated effort
     */
    emitDomChange() {
        this.bus.emit('dom-change');
    }

    /**
     * When called, all controllers that hook into this event will resize in a coordinated effort.
     * 
     * This is to help reduce the number of calls to resize functions and make for cleaner code. 
     */
    emitResize() {
        this.bus.emit('resize');
    }

    /**
     * Returns true if one of the specified arguments matches the name of the current page template. Returns false if
     * no matches were found.
     * 
     * @param possibilities - Variable argument list of names of page templates to check for.
     */
    pageTemplateIs(...possibilities) {
        let match = false;
        
        possibilities.forEach(template => {
            if (this.pageTemplate === template) {
                match = true;
            }
        });
        
        return match;
    }
    
    make(controller, ...args) {
        return new (controller.bind.apply(controller, [controller, this].concat(args)));
    }

    onWindowTouchStart() {
        document.body.classList.add('touch-enabled');

        this.isTouchDevice = true;

        this.bus.emit('touch-enabled');

        window.removeEventListener('touchstart', this.onWindowTouchStart);
    }
    
    addIEClasses() {
        if (this.IEVersion) {
            document.body.classList.add('ie-browser');
            document.body.classList.add('ie-browser-' + this.IEVersion);
        }
    }
}

export default AppController;


// WEBPACK FOOTER //
// ./site/templates/resources/src/js/controllers/app.js